/**
 * Base class for all style engines.
 * Provides shared decomposition and CSS variable resolution logic.
 */

import type { GlobalDesignPanelProps } from '../../types';
import type { PropertyMetadata, AggregatedStyleValues } from './types';
import { VariableResolver } from './variableResolver';
import {
  decomposePropertiesWithDefinitionsAndNesting,
  isShorthandProperty,
} from '../../../../apis/services/editActionsServices/nativeActions/globalDesign/common/decomposer';
import { ThemesProvider } from './ThemesProvider';
import { StyleInvalidValuesMitigator } from './StyleInvalidValuesMitigator';
import { GD_WDS_EXPERIMENT } from '../../experiments';

export abstract class StyleEngineBase {
  protected readonly allowedProperties?: Set<string>;

  constructor(allowedProperties?: string[]) {
    this.allowedProperties = allowedProperties
      ? new Set(allowedProperties)
      : undefined;
  }

  /**
   * Get the allowed properties for this engine (group scope).
   * @returns Array of allowed property keys, or undefined if no scoping
   */
  getAllowedProperties(): string[] | undefined {
    return this.allowedProperties
      ? Array.from(this.allowedProperties)
      : undefined;
  }

  /**
   * Filter a record to only include allowed properties.
   * If no allowedProperties set, returns original record.
   */
  protected filterRecord<T>(record: Record<string, T> = {}): Record<string, T> {
    if (!this.allowedProperties) {
      return record;
    }
    return Object.fromEntries(
      Object.entries(record).filter(([key]) =>
        this.allowedProperties!.has(key),
      ),
    );
  }

  /**
   * Core decomposition logic - shared by all engines.
   * Handles CSS variable resolution, recursive decomposition, and mode discrimination.
   *
   * @param cssPropsRaw - Raw CSS properties values
   * @param customPropsRaw - Raw CSS custom properties values
   * @param cssPropsDefinitions - CSS properties definitions from manifest
   * @param customPropsDefinitions - CSS custom properties definitions from manifest
   * @param metadata - Property metadata
   * @param componentsAPI - Optional components API for CSS variable resolution
   * @returns Aggregated style values with mode discrimination
   */
  protected deriveAggregatedValues(
    cssPropsRaw: Record<string, string>,
    customPropsRaw: Record<string, string>,
    cssPropsDefinitions: Record<string, any>,
    customPropsDefinitions: Record<string, any>,
    metadata: Record<string, PropertyMetadata>,
    componentsAPI?: GlobalDesignPanelProps['componentsAPI'],
  ): AggregatedStyleValues {
    const supportsCSSProps = Object.values(metadata).some(
      (meta) => meta.namespace === 'cssProperties',
    );
    const supportsCustomProps = Object.values(metadata).some(
      (meta) => meta.namespace === 'cssCustomProperties',
    );

    const isGdWdsEnabled =
      componentsAPI?.experimentsAPI?.enabled(GD_WDS_EXPERIMENT) ?? false;

    const themeVariables = componentsAPI
      ? isGdWdsEnabled
        ? new ThemesProvider(componentsAPI).themeVariables
        : new ThemesProvider(componentsAPI).buildThemeVariablesMap(
            cssPropsRaw,
            customPropsRaw,
          )
      : {};

    const mitigator = new StyleInvalidValuesMitigator(themeVariables);

    // When experiment is enabled, use enhanced decomposeFn that restores var() references
    const decomposeFn: (type: string, value: string) => any = isGdWdsEnabled
      ? (type: string, value: string) => {
          const result = mitigator.decompose(type, value);

          if (!result || !value.includes('var(--')) {
            return result;
          }

          const varRefs: {
            varExpr: string;
            resolved: string;
            consumed: boolean;
          }[] = [];
          value.replace(/var\(--([^,\s)]+)\)/g, (match, varName: string) => {
            const resolved = themeVariables[varName];
            if (resolved !== undefined) {
              varRefs.push({ varExpr: match, resolved, consumed: false });
            }
            return match;
          });
          if (varRefs.length === 0) {
            return result;
          }

          const restored: Record<string, string | null> = {};
          for (const [key, val] of Object.entries(result)) {
            if (val === null) {
              restored[key] = val;
              continue;
            }
            const unconsumed = varRefs.find(
              (ref) => !ref.consumed && ref.resolved === val,
            );
            if (unconsumed) {
              restored[key] = unconsumed.varExpr;
              unconsumed.consumed = true;
            } else {
              const reusable = varRefs.find((ref) => ref.resolved === val);
              restored[key] = reusable ? reusable.varExpr : val;
            }
          }
          return restored;
        }
      : (type: string, value: string) => mitigator.decompose(type, value);

    const cssPropsPreprocessed = this.preprocessShorthandVariables(
      cssPropsRaw,
      themeVariables,
      isGdWdsEnabled ? decomposeFn : undefined,
    );
    const customPropsPreprocessed = this.preprocessShorthandVariables(
      customPropsRaw,
      themeVariables,
      isGdWdsEnabled ? decomposeFn : undefined,
      customPropsDefinitions,
    );

    // Always call decompose if definitions exist (even with no values)
    // This ensures eligibility markers (empty arrays) are added for defined properties
    const hasCssPropsDefinitions = Object.keys(cssPropsDefinitions).length > 0;
    const hasCustomPropsDefinitions =
      Object.keys(customPropsDefinitions).length > 0;

    const cssPropsDecomposed = hasCssPropsDefinitions
      ? decomposePropertiesWithDefinitionsAndNesting(
          Object.keys(cssPropsDefinitions),
          cssPropsPreprocessed,
          undefined,
          decomposeFn,
        )
      : {};

    const customPropsDecomposed = hasCustomPropsDefinitions
      ? decomposePropertiesWithDefinitionsAndNesting(
          Object.values(customPropsDefinitions).map((d) => d.cssPropertyType),
          customPropsPreprocessed,
          (key) => customPropsDefinitions?.[key]?.cssPropertyType,
          decomposeFn,
        )
      : {};

    const cssPropsWithResolved = this.addResolvedValuesToDecomposed(
      cssPropsDecomposed,
      themeVariables,
    );
    const customPropsWithResolved = this.addResolvedValuesToDecomposed(
      customPropsDecomposed,
      themeVariables,
    );

    // When experiment is OFF, resolve raw values (original behavior);
    // when ON, keep unresolved var() references in raw.
    const rawCssProps = isGdWdsEnabled
      ? cssPropsRaw
      : VariableResolver.resolveVariablesInRecord(cssPropsRaw, themeVariables);
    const rawCustomProps = isGdWdsEnabled
      ? customPropsRaw
      : VariableResolver.resolveVariablesInRecord(
          customPropsRaw,
          themeVariables,
        );

    if (supportsCSSProps && supportsCustomProps) {
      return {
        mode: 'mixed',
        raw: {
          cssProperties: rawCssProps,
          cssCustomProperties: rawCustomProps,
        },
        decomposed: {
          cssProperties: cssPropsWithResolved,
          cssCustomProperties: customPropsWithResolved,
        },
        metadata,
        definitions: {
          cssProperties: cssPropsDefinitions,
          cssCustomProperties: customPropsDefinitions,
        },
      };
    } else if (supportsCSSProps) {
      return {
        mode: 'cssProperties',
        raw: { cssProperties: rawCssProps },
        decomposed: { cssProperties: cssPropsWithResolved },
        metadata,
        definitions: { cssProperties: cssPropsDefinitions },
      };
    } else {
      return {
        mode: 'cssCustomProperties',
        raw: { cssCustomProperties: rawCustomProps },
        decomposed: { cssCustomProperties: customPropsWithResolved },
        metadata,
        definitions: { cssCustomProperties: customPropsDefinitions },
      };
    }
  }

  /**
   * Resolves shorthand properties that are single CSS variables before decomposition.
   *
   * When decomposeFn is provided (experiment ON): uses smart uniform/mixed check —
   * only resolves when longhands would get different values.
   * When decomposeFn is omitted (experiment OFF): always resolves shorthand variables
   * (original behavior).
   *
   * @param props - Raw properties to preprocess
   * @param themeVariables - Map of resolved theme variables
   * @param decomposeFn - Optional decompose function for smart uniform/mixed check
   * @param customPropsDefinitions - Optional definitions for custom properties
   * @returns Preprocessed properties
   */
  private preprocessShorthandVariables(
    props: Record<string, string>,
    themeVariables: Record<string, string>,
    decomposeFn?: (type: string, value: string) => any,
    customPropsDefinitions?: Record<string, any>,
  ): Record<string, string> {
    const result: Record<string, string> = {};

    for (const [styleItemId, value] of Object.entries(props)) {
      const cssPropertyType = customPropsDefinitions
        ? customPropsDefinitions?.[styleItemId]?.cssPropertyType
        : styleItemId;

      if (
        cssPropertyType &&
        isShorthandProperty(cssPropertyType) &&
        VariableResolver.isSingleCSSVariable(value)
      ) {
        if (!decomposeFn) {
          // Original behavior: always resolve shorthand variables
          result[styleItemId] = VariableResolver.resolveVariableRecursively(
            value,
            themeVariables,
          );
        } else {
          // Smart behavior: only resolve when longhands get different values
          const resolved = VariableResolver.resolveVariableRecursively(
            value,
            themeVariables,
          );

          if (resolved === value) {
            result[styleItemId] = value;
            continue;
          }

          const testDecomposed = decomposePropertiesWithDefinitionsAndNesting(
            [cssPropertyType],
            { [styleItemId]: resolved },
            customPropsDefinitions
              ? (key) => customPropsDefinitions[key]?.cssPropertyType
              : undefined,
            decomposeFn,
          );

          const longhands = Object.keys(testDecomposed).filter(
            (key) => testDecomposed[key].length > 0,
          );
          const firstValue =
            longhands.length > 0
              ? testDecomposed[longhands[0]][0]?.value
              : null;
          const allSame =
            longhands.length > 0 &&
            longhands.every(
              (key) => testDecomposed[key][0]?.value === firstValue,
            );

          result[styleItemId] = allSame ? value : resolved;
        }
      } else {
        result[styleItemId] = value;
      }
    }

    return result;
  }

  /** Adds resolvedValue to each atomic value in decomposed properties. */
  private addResolvedValuesToDecomposed(
    decomposed: Record<string, any[]>,
    themeVariables: Record<string, string>,
  ): Record<string, any[]> {
    const result: Record<string, any[]> = {};
    for (const [key, atomicValues] of Object.entries(decomposed)) {
      result[key] = atomicValues.map((atomic) => ({
        ...atomic,
        resolvedValue: this.resolveAtomicValue(atomic.value, themeVariables),
      }));
    }
    return result;
  }

  /** Resolves CSS variables in an atomic value. */
  private resolveAtomicValue(
    value: string,
    themeVariables: Record<string, string>,
  ): string {
    if (!value.trim().startsWith('var(--')) {
      return value;
    }
    return VariableResolver.resolveVariables(value, themeVariables);
  }
}
